<template>
  <slice
    :id="block._key"
    class="slice--traceability traceability"
    @in="$emit('in')"
    @out="$emit('out')"
  >
    <div class="container">
      <div class="row-md justify-center">
        <div v-if="hasText" class="col-md-8 mb-2 mb-md-6">
          <portable-text :blocks="block.text" class="text-center" />
        </div>
        <div
          ref="traceability"
          v-waypoint="{
            active: true,
            callback: onWaypoint,
            options: intersectionOptions,
          }"
          class="col-md-8 traceability"
        >
          <div>
            <div
              v-for="(item, index) in block.parcours"
              :key="item._key"
              class="traceability__item row-md"
              :class="{
                'traceability__item--first': index === 0,
                'traceability__item--last': index === block.parcours.length - 1,
                'traceability__item--even': index % 2 === 0,
                'traceability__item--odd': index % 2 === 1,
              }"
            >
              <div class="line"></div>
              <div class="col-md-6 traceability__item__image d-none d-md-block">
                <lazy-image
                  :image="prepareImage(item.image)"
                  provider="sanity"
                  sizes="330px"
                />
              </div>
              <div
                class="col-md-6 traceability__item__text d-md-flex align-center text-center text-md-left"
              >
                <div>
                  <div ref="circle" class="circle"></div>
                  <p
                    v-if="item.kilometers !== null"
                    class="h6 text-secondary mb-0 mb-md-0-6"
                  >
                    {{ item.kilometers }}&nbsp;KM
                  </p>

                  <portable-text
                    v-if="item.title && item.title.length > 0"
                    :blocks="item.title"
                  />

                  <div class="traceability__item__image d-md-none mb-0-6">
                    <lazy-image
                      :image="prepareImage(item.image)"
                      provider="sanity"
                      sizes="330px"
                    />
                  </div>

                  <p class="text-small mb-0-6">
                    <strong class="d-block">{{ item.factory_name }}</strong>
                    <strong class="d-block">{{ item.factory_region }}</strong>
                  </p>

                  <portable-text
                    v-if="item.impact && item.impact.length > 0"
                    :blocks="item.impact"
                    class="text-small"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            ref="line"
            class="hover__line"
            :style="{ height: `${h}px` }"
          ></div>
        </div>
      </div>
    </div>
  </slice>
</template>

<script>
import Slice from '@/components/Slices/Slice'
import PortableText from '@/components/RichText/PortableText'
import LazyImage from '@/components/Image/LazyImage'

import { getSanityBunnyImageLink } from '@/utils/image'

export default {
  name: 'BlockTracabilite',
  components: {
    Slice,
    LazyImage,
    PortableText,
  },
  props: {
    block: { type: Object, required: true },
  },
  data() {
    return {
      h: 0,
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0, 1], // [0.25, 0.75] if you want a 25% offset!
      },
    }
  },
  computed: {
    hasText() {
      return this.block.text && this.block.text.length > 0
    },
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.updateHeightOfLine)
  },
  methods: {
    prepareImage(image) {
      return {
        url: getSanityBunnyImageLink(image, {
          width: 330,
          height: 256,
        }),
        dimensions: {
          width: 330,
          height: 256,
        },
      }
    },
    onWaypoint({ going }) {
      if (going === this.$waypointMap.GOING_IN) {
        this.add()
      }

      if (going === this.$waypointMap.GOING_OUT) {
        this.remove()
      }
    },
    add() {
      if (this.$refs.traceability && this.$refs.circle) {
        window.addEventListener('scroll', this.updateHeightOfLine)
      }
    },
    remove() {
      window.removeEventListener('scroll', this.updateHeightOfLine)
    },
    updateHeightOfLine() {
      const t = this.$refs.traceability

      const th = t.getBoundingClientRect().top
      const windowSize = window.innerHeight

      this.h = -th + windowSize / 2
      this.activateCircle()
    },
    activateCircle() {
      const t = this.$refs.traceability
      const th = t.getBoundingClientRect().top

      if (this.$refs.circle) {
        this.$refs.circle.forEach((c) => {
          const ct = c.getBoundingClientRect().top

          const hc = Math.abs(th - ct)

          if (hc <= this.h) {
            c.classList.add('circle--active')
          } else {
            c.classList.remove('circle--active')
          }
        })
      }
    },
  },
}
</script>

<style lang="scss">
.traceability {
  position: relative;
  overflow: hidden;

  &__item {
    overflow: hidden;

    @include mq($until: tablet) {
      position: relative;
      padding-top: rem($spacing * 3);
      padding-bottom: rem($spacing);

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 50%;
        width: rem(1px);
        height: rem($spacing * 3);
        background-image: url('~/assets/img/dash-vertical.svg');
        background-size: 1px auto;
        background-repeat: repeat-y;
      }
    }

    @include mq($from: tablet) {
      padding-bottom: rem($spacing * 3);
    }

    .line {
      @include mq($from: tablet) {
        position: absolute;
        top: 0;
        left: 50%;
        width: rem(1px);
        height: 100%;
        background-image: url('~/assets/img/dash-vertical.svg');
        background-size: 1px auto;
        background-repeat: repeat-y;
      }
    }

    &__image {
      @include mq($from: tablet) {
        padding-right: rem($spacing * 2);
      }

      .lazy-image-container {
        max-width: rem(360px);
        margin-left: auto;

        @include mq($until: tablet) {
          margin-right: auto;
        }
      }
    }

    &__text {
      @include mq($from: tablet) {
        padding-left: rem($spacing * 2);
      }

      > div {
        position: relative;
      }

      .circle {
        width: rem(13px);
        height: rem(13px);
        border: 2px solid var(--secondary-color);
        background: var(--tertiary-color);
        border-radius: 50%;
        z-index: 3;
        transition: all 0.3s;

        &--active {
          background: var(--secondary-color);
        }

        @include mq($until: tablet) {
          margin: 0 auto rem($spacing);
        }

        @include mq($from: tablet) {
          position: absolute;
          top: rem(9px);
          left: rem(-36px);
        }
      }

      .rich-text {
        p {
          margin-bottom: rem($spacing * 0.6);

          &:last-child {
            margin-bottom: 0;
          }
        }
      }
    }

    @include mq($until: tablet) {
      &--first {
        padding-top: 0;

        &::before {
          display: none;
        }

        .circle {
          display: none;
        }
      }
    }

    @include mq($from: tablet) {
      &--first,
      &--last {
        .traceability__item__text {
          > div {
            &::after {
              content: '';
              position: absolute;
              width: rem(6px);
              height: rem(500px);
              background: var(--tertiary-color);
              z-index: 2;
            }
          }
        }
      }

      &--first {
        .traceability__item__text {
          > div {
            &::after {
              left: rem(-32px);
              bottom: 100%;
              margin-bottom: rem(-12px);
            }
          }
        }
      }

      &--last {
        padding-bottom: 0;

        .traceability__item__text {
          > div {
            &::after {
              right: rem(-32px);
              top: rem(9px);
            }
          }
        }
      }

      &--odd {
        .traceability__item__image {
          order: 2;
          padding-right: 0;
          padding-left: rem($spacing * 2);

          .lazy-image-container {
            margin-left: 0;
          }
        }

        .traceability__item__text {
          order: 1;
          padding-left: 0;
          padding-right: rem($spacing * 2);
          text-align: right;
          justify-content: flex-end;

          .circle {
            left: auto;
            right: rem(-37px);
          }
        }
      }

      &--even {
        &.traceability__item--last {
          .traceability__item__text {
            > div {
              &::after {
                left: rem(-32px);
                right: auto;
              }
            }
          }
        }
      }
    }
  }

  .hover__line {
    @include mq($until: tablet) {
      display: none;
    }

    @include mq($from: tablet) {
      position: absolute;
      left: 50%;
      top: 0;
      bottom: 0;
      width: rem(1px);
      background: var(--secondary-color);
      z-index: 1;
    }
  }
}
</style>
